import {createMuiTheme, darken, fade} from "@material-ui/core";
import {responsiveFontSizes} from "@material-ui/core/styles";
import {grey} from "@material-ui/core/colors";
import {BP_MEDIUM, breakpoints} from "./breakpoints";
import nestedProperty from "nested-property";
import {SHARED_CDN_PATH} from "Setup/config";

const defaultLayoutPalette = {
    xlight: '#FCFCF8',
    light: '#E8EBE2',
    main: '#CCCDC4',
    dark: '#46473D',
    xdark: '#202019',
    contrastText: "#fff"
};

// const accentPalette = {
//     light: "#A5FFDE",
//     main: "#44F2C8",
//     dark: "#1B876D",
//     contrastText: "#fff"
// }

export const neutralPalette = {
    ...grey,
    xlight: '#FCFCF8',
    light: '#E8EBE2',
    main: '#CCCDC4',
    dark: '#46473D',
    xdark: '#202019',
};

const checkForNestedThemeValue = (object, path, fallback) => {
    const value = nestedProperty.get(object, path);
    if (value) return value;

    return fallback;
}

const createTheme = (themeSettings = {}) => {
    const theme = createMuiTheme({...themeSettings, breakpoints});

    const layoutPalette = checkForNestedThemeValue(theme, "palette.layout", defaultLayoutPalette);
    const PaperHeaderBackground = checkForNestedThemeValue(theme.overrides, "Modal.titleBackground", theme.palette.primary.main);
    const PaperHeaderTextColor = checkForNestedThemeValue(theme.overrides, "Modal.titleColor", theme.palette.primary.contrastText);
    const PaperModalBorderColor = checkForNestedThemeValue(theme.overrides, "Modal.borderColor", theme.palette.primary.main);

    return responsiveFontSizes(
        createMuiTheme({
            id: 'default',
            palette: {
                layout: defaultLayoutPalette,
                // accent: accentPalette,
                neutral: neutralPalette,
            },
            layout: {
                brandPath: '',
                logo: '',
                contrastLogo: '',
                background: "none",
                backgroundMobile: "none",
            },
            typography: {
                button: {
                    fontWeight: 500,
                    textTransform: 'none',
                    [theme.breakpoints.down(theme.breakpoints.values.medium)]: {
                        fontSize: '0.8rem',
                    },
                },
                caption: {},
            },
            overrides: {
                /*
                We can define default theme settings for our components in here.
                These values are populated using the site theme and are passed
                to the component using theme.overrides[COMPONENT] in makeStyles().
                Once defined here, any site can override these in their respective
                theme file.
                */
                Buttons: {
                    // If we need to reverse the buttons - likely to change.
                    primary: 'primary',
                    secondary: 'secondary',
                },
                RegistrationForm: {
                    buttonGroupBackground: layoutPalette.xdark,
                    buttonBackground: layoutPalette.main,
                    contrastText: layoutPalette.contrastText,
                    outlinedButtonColor: neutralPalette.dark,
                    inputBorderRadius: 4,
                    steps: {
                        color: '#958E99',
                        activeColor: neutralPalette.xdark,
                        completeColor: layoutPalette.main,
                        incompleteColor: '#DCDADD',
                    },
                },
                rgTools: {
                    logo: theme.layout.logo,
                    background: layoutPalette.dark,
                    contentBackground: neutralPalette.xlight,
                    navLinkBackground: layoutPalette.main,
                    iconColor: layoutPalette.light,
                    shieldColor: '#38317D',
                    shieldColorMobile: '#fff',
                    contrastText: layoutPalette.contrastText,
                    sideBarHeader: layoutPalette.xdark,
                    buttonBorderRadius: theme.spacing(3),
                    linkColor: neutralPalette.xlight,
                    activeLink: layoutPalette.xdark,
                },
                TopBar: {
                    background: layoutPalette.dark,
                    contrastText: layoutPalette.contrastText,
                    logoWidth: 200,
                    logoWidthSmall: 150,
                },
                PlayerBar: {
                    background: layoutPalette.xdark,
                    contrastText: layoutPalette.contrastText,
                    borderBottom: "none",
                    inputBackground: layoutPalette.dark,
                    inputColor: layoutPalette.contrastText,
                },
                LoginPage: {
                    sideContrastColor: layoutPalette.contrastText,
                    sideLogoWidth: 180,
                    formContrastColor: neutralPalette.dark,
                    formBackground: 'white',
                    linkColor: layoutPalette.main,
                },
                NotificationsPanel: {
                    background: layoutPalette.xdark,
                    contrastText: layoutPalette.contrastText,
                    dividerColor: neutralPalette.main,
                    backdropFilter: 'unset',
                    straplineBackground: '#E8EBE2',
                    notificationIconColor: "white",
                    notificationIconHover: theme.palette.primary.main,
                    paperbackground: '#fff',
                },
                RulesStrapline: {
                    background: layoutPalette.xdark,
                },
                GamificationModal: {
                    maxWidth: 800,
                    backgroundColor: layoutPalette.dark,
                    textColor: "white",
                    reelImage: `url(${SHARED_CDN_PATH}/DailyDealImages/sprite.png)`,
                },
                DailyDealV2Modal: {
                    maxWidth: 1200,
                    backgroundColor: layoutPalette.dark,
                    textColor: "white",
                    titleTextColor: "white",
                    borderColor: layoutPalette.main,
                    buttonType: "primary"
                },
                // FreeSpinsGame: {
                //     backgroundColor: layoutPalette.dark,
                //     textColor: "white",
                // },
                CustomSkeleton: {
                    background: "#fff3"
                },
                // Content is the area between the PlayerBar and the Footer
                // Useful when having to set the correct text color to contrast
                // with the background of the lobby.
                Content: {
                    contrastText: layoutPalette.contrastText,
                },
                LobbyNavigation: {
                    background: layoutPalette.dark,
                    backgroundHover: `rgba(255, 255, 255, 0.1)`,
                    defaultText: layoutPalette.contrastText,
                    contrastText: theme.palette.primary.main,
                    contrastTextHover: theme.palette.primary.main,
                    contrastTextActive: theme.palette.secondary.main,
                },
                BingoCards: {
                    background: layoutPalette.light,
                    borderColor: darken(layoutPalette.light, 0.25),
                    borderRadius: theme.shape.borderRadius,
                    titleColor: '#484848',
                    prizeColor: '#484848',
                    iconColor: '#958E99',
                    specialIconColor: '#DECB8B',
                    specialBorderColor: 'transparent',
                    fontFamily: theme.typography.headersFontFamily,

                    pjpBarBackgroundColor: theme.palette.secondary.main,
                    pjpBarTextColor: "black",
                },
                LobbyGameTiles: {
                    tileSpacing: theme.spacing(0.5),
                    borderRadius: theme.spacing(3),
                    borderPadding: theme.spacing(1),
                    borderColor: layoutPalette.main,
                    borderColorOpacity: 0.5,
                    borderColorHoverOpacity: 0.4,
                    backgroundColor: layoutPalette.main
                },
                Modal: {
                    titleBackground: layoutPalette.main,
                    titleColor: layoutPalette.contrastText,
                    borderColor: darken(layoutPalette.main, 0.25),
                },
                BankingModal: {
                    color: "white",
                    backgroundColor: layoutPalette.main,
                    actionTabColor: theme.palette.primary.main,
                    circularLoadingColor: "primary"
                },
                LoadingModal: {
                    contrastText: layoutPalette.contrastText,
                    background: fade(layoutPalette.main, 0.5),
                    sprite: {
                        width: 0,
                        height: 0,
                        canvasHeight: 0,
                        steps: '0 steps(0) infinite',
                        smallScale: 1,
                        largeScale: 1
                    }
                },
                GameFrame: {
                    sideBarBackground: layoutPalette.xdark,
                    frameBackground: layoutPalette.dark,
                    contrastText: layoutPalette.contrastText,
                    searchBackground: layoutPalette.dark,
                },
                Footer: {
                    background: layoutPalette.xdark,
                    contrastText: layoutPalette.contrastText,
                    linkColor: layoutPalette.light,
                    opacity: 0.7
                },
                AccountSideMenu: {
                    background: layoutPalette.dark,
                    contrastText: neutralPalette.xlight,
                    backdropFilter: 'unset',
                    dividerColor: layoutPalette.main,
                    balanceBox: {
                        color: layoutPalette.contrastText,
                        background: layoutPalette.main,
                        dividerColor: layoutPalette.dark,
                        borderRadius: theme.shape.borderRadius,
                        borderColor: layoutPalette.contrastText,
                    },
                    navigationItems: {
                        color: layoutPalette.contrastText,
                        iconColor: layoutPalette.main,
                        background: layoutPalette.dark,
                    },
                    message: {
                        background: fade(layoutPalette.main, 0.7),
                        color: layoutPalette.contrastText,
                        iconColor: neutralPalette.xlight,
                        hoverColor: layoutPalette.xlight,
                        dividerColor: layoutPalette.dark,

                    },
                    accountSectionContainer: {
                        background: layoutPalette.dark,
                        headerBackground: layoutPalette.dark,
                        contrastText: layoutPalette.contrastText
                    },
                },
                MessagesSideMenu: {
                    background: layoutPalette.dark,
                },
                SideBarMenu: {
                    logoWidth: 150,
                    logoWidthSmall: 150,
                    headerBackground: layoutPalette.dark,
                    background: layoutPalette.xdark,
                    contrastText: layoutPalette.contrastText,
                    closeIconColor: neutralPalette.xlight,
                    iconColor: layoutPalette.contrastText,
                    backdropFilter: 'unset'
                },
                PromotionCard: {
                    border: "none",
                    borderRadius: theme.spacing(2),
                    background: fade(layoutPalette.contrastText, 0.4),
                    contrastText: layoutPalette.contrastText,
                    backdropFilter: 'blur(12px)',
                    cardAction: {
                        justifyContent: "center"
                    }
                },
                filterTab: {
                    dividerColor: '1px solid #D2D2D2',
                    border: `solid 1px ${layoutPalette.dark}`,
                    borderRadius: 100,
                    margin: theme.spacing(2, 0.55),
                    background: '#F2F2F2',
                    color: layoutPalette.dark,
                    activeBackground: layoutPalette.dark,
                    activeColor: layoutPalette.contrastText,
                },
                RewardsPage: {
                    borderRadius: theme.spacing(2),
                    headerBackground: layoutPalette.main,
                    background: fade(layoutPalette.contrastText, 0.4),
                    headerTextColor: layoutPalette.contrastText,
                    backdropFilter: 'blur(12px)',
                    showBadgeLogo: false,
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
                    strapline: {
                        background: 'none',
                        textColor: neutralPalette.xdark,
                    },
                    content: {
                        color: "black"
                    },
                    tabs: {
                        background: layoutPalette.main,
                        activeColor: "white",
                        inactiveColor: "black",
                    },
                    indicator: {
                        borderRadius: 30,
                        background: layoutPalette.main,
                    },
                    cards: {
                        background: layoutPalette.xlight,
                        textColor: neutralPalette.xdark,
                    },
                    infoCards: {
                        color: neutralPalette.xdark,
                        background: fade(layoutPalette.contrastText, 0.4),
                        ctaColor: layoutPalette.main,
                        borderColor: layoutPalette.main,
                    }
                },
                LevelProgressBar: {
                    unfilledColor: '#DCDADD',
                    fillColor: layoutPalette.xdark,
                    color: layoutPalette.contrastText,
                },
                HoldingPage: {
                    backgroundColor: layoutPalette.dark,
                    logoWidth: 200,
                    logoWidthSmall: 160,
                },
                LandingPage: {
                    backgroundColor: layoutPalette.dark,
                    background: layoutPalette.dark,
                    backgroundMobile: layoutPalette.dark,
                    logoWidth: 200,
                    logoWidthSmall: 150,
                    jackpotBackground: layoutPalette.xlight,
                    jackpotTextColor: layoutPalette.dark,
                    slotMachinebackground: layoutPalette.xdark,
                    slotMachineBorder: "#000 solid 3px",
                    slotMachineFontColor: neutralPalette.dark,
                    slotMachineAccentColor: layoutPalette.dark,
                    offer: {
                        justifyContent: 'center',
                        fontFamily: 'inherit',
                        fontWeight: 800,
                        color: layoutPalette.dark,
                        largeFontSize: '2.25rem',
                    }
                },
                NewPlayerHomepage: {
                    logoWidth: 150,
                    logoWidthSmall: 100,
                    logoJustify: 'start',
                    TopBarBackground: 'transparent',
                    background: layoutPalette.dark,
                    backgroundMobile: layoutPalette.dark,
                    fallbackBackgroundColor: layoutPalette.dark,
                    rootBackground: 'none',
                    color: layoutPalette.contrastText,
                    sectionHeight: 700,
                    offer: {
                        color: layoutPalette.contrastText,
                        textAlign: 'center',

                        mediumFontSize: '1.5rem',
                        fontWeight: 600,
                    },
                },
                TrustPilotSection: {
                    background: 'transparent',
                    backgroundMobile: 'white',
                    theme: 'light',
                    color: neutralPalette.dark,
                    iconColor: theme.palette.secondary.main,
                },
                ShortForm: {
                    titleColor: layoutPalette.contrastText,
                    background: layoutPalette.xdark,
                    inputBackground: '#fff',
                    inputColor: layoutPalette.main,
                },
                RGInfoPage: {
                    root: {
                        background: theme.layout.background
                    },
                    topBar: {
                        scrollBackground: layoutPalette.main,
                        color: neutralPalette.main,
                        hoverColor: neutralPalette.xlight,
                        menuIconColor: layoutPalette.contrastText
                    },
                    card: {
                        background: 'rgba(255, 255, 255, 0.9)',
                    },
                    rgShieldSection: {
                        background: 'transparent',
                        color: layoutPalette.contrastText
                    },
                    rgShieldText: {
                        color: layoutPalette.contrastText
                    },
                    lightBackground: {
                        background: neutralPalette.light,
                        color: neutralPalette.dark
                    },
                    imageBackground: {
                        background: layoutPalette.dark,
                    },
                    contrastHeaderText: {
                        color: layoutPalette.contrastText
                    }
                },
                SaferGamblingPage: {
                    topBarBackground: layoutPalette.xdark,
                    background: layoutPalette.xdark,
                    backgroundImage: layoutPalette.xdark,
                    titleColor: layoutPalette.contrastText,
                },
                AffordabilityCalculator: {
                    stepProgressWrapper: {
                        stepIndicatorBackgroundActive: layoutPalette.main,
                        stepIndicatorActiveFontColor: layoutPalette.contrastText
                    }
                },
                LoyaltyTokens: {
                    pointsDisplay: {
                        backgroundColor: theme.palette.primary.dark,
                        color: theme.palette.primary.contrastText,
                    },
                    slider: {
                        trackColor: theme.palette.primary.dark,
                        railColor: neutralPalette.light,
                        textColor: theme.palette.primary.contrastText,
                    }
                },
                CookiesNotification: {
                    color: "white",
                    background: "#313131"
                },
                ChangelogPage: {
                    background: neutralPalette.xlight,
                    textColor: theme.palette.primary.dark,
                },
                DirectMarketing:{
                    Modal:{
                        attentionBackgroundColor: theme.palette.layout.light
                    }
                },
                /*
                Below are the overrides for Mui components, use the classNames in
                the Mui docs to override nested styles.
                */
                MuiCssBaseline: {
                    '@global': {
                        'a:not(.MuiButtonBase-root, .MuiTypography-root)': {
                            color: theme.palette.primary.main,
                            textDecoration: "underline"
                        },
                        '.MuiPaper-root a:not(.MuiButtonBase-root, .MuiTypography-root)': {
                            color: theme.palette.primary.dark,
                            textDecoration: "underline"
                        },
                    },
                },
                MuiMenuItem: {
                    root: {
                        minHeight: 20
                    }
                },
                MuiOutlinedInput: {
                    root: {
                        "&:hover $notchedOutline": {
                            borderColor: fade(layoutPalette.main, 0.4),
                        },
                        "&$focused $notchedOutline": {
                            borderColor: fade(layoutPalette.main, 0.8),
                        }
                    }
                },
                MuiButton: {
                    // shadows: ["none"],
                    iconSizeMedium: {
                        "& > *:first-child": {
                            fontSize: 24
                        }
                    }
                },
                MuiDialog: {
                    root: {
                        backdropFilter: "blur(2px)",

                        '& .MuiDialogContent-root::-webkit-scrollbar': {
                            width: theme.spacing(1)
                        },
                        '& .MuiDialogContent-root::-webkit-scrollbar-track': {
                            backgroundColor: fade(PaperHeaderBackground, 0.3),
                            // borderRadius: 4,
                        },
                        '& .MuiDialogContent-root::-webkit-scrollbar-thumb': {
                            backgroundColor: PaperHeaderBackground,
                            // borderRadius: 4,
                        }
                    }
                },
                MuiDialogContent: {
                    root: {
                        padding: theme.spacing(2, 3),
                        [theme.breakpoints.down(BP_MEDIUM)]: {
                            padding: theme.spacing(1)
                        },
                    }
                },
                MuiDialogActions: {
                    root: {
                        padding: theme.spacing(2, 3),
                    }
                },
                MuiInputBase: {
                    input: {
                        // Fix for Chrome style rule that doesn't respect font settings
                        '&:-webkit-autofill::first-line': {
                            fontFamily: theme?.typography?.fontFamily,
                            fontSize: '1rem'
                        }
                    }
                },
                MuiPaper: {
                    root: {
                        border: `1px solid ${PaperModalBorderColor}`,
                        '&.MuiDialog-paperScrollPaper': {
                            [theme.breakpoints.up(theme.breakpoints.values.medium)]: {
                                borderRadius: theme.spacing(3),
                            },
                        },
                        '&::-webkit-scrollbar': {
                            width: theme.spacing(1),
                        },
                        '&::-webkit-scrollbar-track': {
                            '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
                            backgroundColor: fade(PaperHeaderBackground, 0.3),
                            borderRadius: 4,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: PaperHeaderBackground,
                            borderRadius: 4,
                        }
                    }
                },
                MuiSwitch: {
                    root: {
                        width: 80,
                        height: 48,
                        padding: 8,
                        "& .Mui-checked": {
                            "&$switchBase": {
                                "transform": "translateX(32px)"
                            }
                        }
                    },
                    switchBase: {
                        padding: 11,
                        color: layoutPalette.main,
                    },
                    thumb: {
                        width: 26,
                        height: 26,
                        backgroundColor: '#C2CFD1',
                    },
                    track: {
                        backgroundColor: '#f2f2f2',
                        opacity: 1,
                        borderRadius: 20,
                        position: 'relative',
                        '&:before, &:after': {
                            display: 'inline-block',
                            position: 'absolute',
                            top: '50%',
                            width: '50%',
                            transform: 'translateY(-50%)',
                            textAlign: 'center',
                            fontSize: 12,

                        },
                        '&:before': {
                            content: '"Yes"',
                            left: 4,
                            opacity: 0,
                            color: 'white',
                        },
                        '&:after': {
                            content: '"No"',
                            right: 4,
                            color: layoutPalette.xdark,
                        },
                        "$checked$colorPrimary + &": {
                            opacity: 1.0,
                            background: theme.palette.primary.xdark,
                            '&:before': {
                                opacity: 1,
                            },
                        },
                        "$checked$colorSecondary + &": {
                            opacity: 1.0,
                            background: theme.palette.secondary.xdark,
                            '&:before': {
                                opacity: 1,
                            },
                        },
                    },


                },
                MuiTableHead: {
                    root: {
                        background: PaperHeaderBackground,
                        '& .MuiTableCell-head': {
                            color: PaperHeaderTextColor
                        }
                    }
                },
                MuiTableCell: {
                    root: {
                        fontSize: 12,
                        padding: 4
                    }
                },
                MuiTableSortLabel: {
                    root: {
                        color: PaperHeaderTextColor,

                        // if you want to have icons visible permanently
                        // '& $icon': {
                        //   opacity: 1,
                        //   color: primaryMain
                        // },

                        "&:hover": {
                            color: PaperHeaderTextColor,

                            '&& $icon': {
                                opacity: 1,
                                color: PaperHeaderTextColor,
                            },
                        },
                        "&$active": {
                            color: PaperHeaderTextColor,

                            // && instead of & is a workaround for https://github.com/cssinjs/jss/issues/1045
                            '&& $icon': {
                                opacity: 1,
                                color: PaperHeaderTextColor,
                            },
                        },
                    },
                }
            },
        }, theme)
    );
};


export default createTheme;
